// src/app/slice/quotationSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import quotationService from "../../features/quotation/service/quotationService";
import {
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
} from "../../features/quotation/types/quotationTypes";

// Async Thunks
export const fetchQuotations = createAsyncThunk(
  "quotation/fetchQuotations",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await quotationService.getQuotations(filters);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.FETCH_FAILED);
    }
  },
);

export const fetchQuotationById = createAsyncThunk(
  "quotation/fetchQuotationById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await quotationService.getQuotationById(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.FETCH_FAILED);
    }
  },
);

export const createQuotation = createAsyncThunk(
  "quotation/createQuotation",
  async (quotationData, { rejectWithValue }) => {
    try {
      const response = await quotationService.createQuotation(quotationData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.CREATE_FAILED);
    }
  },
);

export const updateQuotation = createAsyncThunk(
  "quotation/updateQuotation",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await quotationService.updateQuotation(id, data);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.UPDATE_FAILED);
    }
  },
);

export const deleteQuotation = createAsyncThunk(
  "quotation/deleteQuotation",
  async (id, { rejectWithValue }) => {
    try {
      await quotationService.deleteQuotation(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.DELETE_FAILED);
    }
  },
);

export const updateQuotationStatus = createAsyncThunk(
  "quotation/updateQuotationStatus",
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await quotationService.updateStatus(id, status);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.message || ERROR_MESSAGES.STATUS_UPDATE_FAILED,
      );
    }
  },
);

export const sendQuotation = createAsyncThunk(
  "quotation/sendQuotation",
  async (id, { rejectWithValue }) => {
    try {
      const response = await quotationService.sendQuotation(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.SEND_FAILED);
    }
  },
);

export const convertToInvoice = createAsyncThunk(
  "quotation/convertToInvoice",
  async (id, { rejectWithValue }) => {
    try {
      const response = await quotationService.convertToInvoice(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.CONVERT_FAILED);
    }
  },
);

export const fetchQuotationStats = createAsyncThunk(
  "quotation/fetchQuotationStats",
  async (_, { rejectWithValue }) => {
    try {
      const response = await quotationService.getQuotationStats();
      return response;
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.FETCH_FAILED);
    }
  },
);

const initialState = {
  quotations: [],
  selectedQuotation: null,
  loading: false,
  error: null,
  success: null,
  filters: {
    status: "all",
    search: "",
    dateRange: { start: null, end: null },
    clientId: "all",
    minAmount: null,
    maxAmount: null,
    tags: [],
  },
  sortBy: "date",
  sortOrder: "desc",
  stats: {
    total: 0,
    draft: 0,
    sent: 0,
    viewed: 0,
    approved: 0,
    rejected: 0,
    expired: 0,
    converted: 0,
    totalValue: 0,
    averageValue: 0,
    conversionRate: 0,
    responseRate: 0,
  },
};

const quotationSlice = createSlice({
  name: "quotation",
  initialState,
  reducers: {
    setQuotations: (state, action) => {
      state.quotations = action.payload;
    },
    setSelectedQuotation: (state, action) => {
      state.selectedQuotation = action.payload;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },
    setSort: (state, action) => {
      state.sortBy = action.payload.sortBy || state.sortBy;
      state.sortOrder = action.payload.sortOrder || state.sortOrder;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
    resetQuotationState: (state) => {
      return { ...initialState };
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Quotations
      .addCase(fetchQuotations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQuotations.fulfilled, (state, action) => {
        state.loading = false;
        state.quotations = action.payload;
      })
      .addCase(fetchQuotations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Quotation by ID
      .addCase(fetchQuotationById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQuotationById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedQuotation = action.payload;
      })
      .addCase(fetchQuotationById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create Quotation
      .addCase(createQuotation.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(createQuotation.fulfilled, (state, action) => {
        state.loading = false;
        state.quotations.unshift(action.payload);
        state.selectedQuotation = action.payload;
        state.success = SUCCESS_MESSAGES.CREATE_SUCCESS;
      })
      .addCase(createQuotation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update Quotation
      .addCase(updateQuotation.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateQuotation.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.quotations.findIndex(
          (q) => q.id === action.payload.id,
        );
        if (index !== -1) {
          state.quotations[index] = action.payload;
        }
        state.selectedQuotation = action.payload;
        state.success = SUCCESS_MESSAGES.UPDATE_SUCCESS;
      })
      .addCase(updateQuotation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete Quotation
      .addCase(deleteQuotation.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(deleteQuotation.fulfilled, (state, action) => {
        state.loading = false;
        state.quotations = state.quotations.filter(
          (q) => q.id !== action.payload,
        );
        if (state.selectedQuotation?.id === action.payload) {
          state.selectedQuotation = null;
        }
        state.success = SUCCESS_MESSAGES.DELETE_SUCCESS;
      })
      .addCase(deleteQuotation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update Status
      .addCase(updateQuotationStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateQuotationStatus.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.quotations.findIndex(
          (q) => q.id === action.payload.id,
        );
        if (index !== -1) {
          state.quotations[index] = action.payload;
        }
        if (state.selectedQuotation?.id === action.payload.id) {
          state.selectedQuotation = action.payload;
        }
        state.success = SUCCESS_MESSAGES.STATUS_UPDATE_SUCCESS;
      })
      .addCase(updateQuotationStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Send Quotation
      .addCase(sendQuotation.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(sendQuotation.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.quotations.findIndex(
          (q) => q.id === action.payload.id,
        );
        if (index !== -1) {
          state.quotations[index] = action.payload;
        }
        if (state.selectedQuotation?.id === action.payload.id) {
          state.selectedQuotation = action.payload;
        }
        state.success = SUCCESS_MESSAGES.SEND_SUCCESS;
      })
      .addCase(sendQuotation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Convert to Invoice
      .addCase(convertToInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(convertToInvoice.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.quotations.findIndex(
          (q) => q.id === action.payload.id,
        );
        if (index !== -1) {
          state.quotations[index] = action.payload;
        }
        if (state.selectedQuotation?.id === action.payload.id) {
          state.selectedQuotation = action.payload;
        }
        state.success = SUCCESS_MESSAGES.CONVERT_SUCCESS;
      })
      .addCase(convertToInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Stats
      .addCase(fetchQuotationStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQuotationStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload;
      })
      .addCase(fetchQuotationStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setQuotations,
  setSelectedQuotation,
  setFilters,
  clearFilters,
  setSort,
  clearError,
  clearSuccess,
  resetQuotationState,
} = quotationSlice.actions;

export default quotationSlice.reducer;
